/**
 * The workspace's list and grid of files (and, where it applies, the folders
 * beside them). Shared by Browse and Search.
 *
 * What a file OFFERS — Move, Give an arche access, Delete, Restore… — is not
 * decided here. Each row renders the action set `useFileActions` built for
 * it, so the file-area rules for "what can I do with this file" live in one
 * place for every view.
 */
import {
  ArrowRightLeft,
  Download as DownloadIcon,
  File as FileIcon,
  Folder as FolderIcon,
  Info,
  LayoutGrid,
  List as ListIcon,
  Lock,
  RotateCcw,
  ShieldCheck,
  Trash2,
  X,
} from 'lucide-react';
import type { VaultDocumentDto, VaultDocumentFolderDto } from '@/lib/api';
import { formatBytes } from '@/components/files/file-dialog/format';
import { scrubForDisplay } from '@/components/files/file-dialog/display-safety';
import { SensitivityBadge } from '../SensitivityBadge';
import { VisibilityBadge, EditedByAppBadge } from '../VisibilityBadge';
import { isImageMime } from '../../lib/file-display';
import type { FileActionSet } from '../../hooks/useFileActions';
import { DocumentThumbnail } from './DocumentThumbnail';
import { DRAG_DOC_MIME, useFolderRowDnd } from './folder-drag';

export interface DocumentsViewProps {
  mode: 'list' | 'grid';
  loading: boolean;
  documents: VaultDocumentDto[];
  /** All folders for the active Holder. The grid uses these to render
   * sibling folder tiles alongside documents (Explorer-style). */
  folders: VaultDocumentFolderDto[];
  /** Currently-selected folder id, or `null` for Holder root. Used by
   * the grid to filter which folders are direct children. */
  currentFolderId: string | null;
  /** Show the current folder's sub-folders before its files. Default true;
   *  a search result lists files only. */
  showFolders?: boolean;
  isTrash: boolean;
  /** Default click — opens the inline preview/viewer for any file type. */
  onOpenDocument: (document: VaultDocumentDto) => void;
  onOpenFolder: (folderId: string) => void;
  /** The actions a file offers here (`useFileActions().actionsFor`). */
  actionsFor: (document: VaultDocumentDto) => FileActionSet;
  /** Where each file sits ("Documents / Projects"), for views that span
   *  folders. Omitted = not shown. */
  locationOf?: (document: VaultDocumentDto) => string;
  /** Drop a dragged document onto a sibling folder tile (grid view).
   * The folder tree handles its own drop targets via FolderTree. */
  onDropOnFolder?: (folderId: string, documentId: string) => void;
  /** Copy for an empty, non-Trash view. */
  emptyMessage?: string;
}

export function DocumentsView(props: DocumentsViewProps) {
  if (props.loading) {
    return <p className="text-sm text-muted-foreground">Loading documents…</p>;
  }
  // Both views surface direct child folders of the current selection
  // (Windows-Explorer convention) — except in the Trash view, which is
  // a folder-agnostic flat list of purge candidates.
  const childFolders =
    !props.isTrash && props.showFolders !== false
      ? props.folders.filter((f) => (f.parentFolderId ?? null) === props.currentFolderId)
      : [];
  const empty = props.documents.length === 0 && childFolders.length === 0;
  if (empty) {
    return (
      <div className="rounded border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          {props.isTrash
            ? 'Trash is empty.'
            : (props.emptyMessage ??
              'No documents here. Drag-and-drop a file above to get started.')}
        </p>
      </div>
    );
  }
  if (props.mode === 'list') return <DocumentsTable {...props} childFolders={childFolders} />;
  return <DocumentsGrid {...props} childFolders={childFolders} />;
}

// ---------------------------------------------------------------------------
// Table view
// ---------------------------------------------------------------------------

function DocumentsTable({
  documents,
  isTrash,
  childFolders,
  onOpenDocument,
  onOpenFolder,
  actionsFor,
  locationOf,
  onDropOnFolder,
}: DocumentsViewProps & { childFolders: VaultDocumentFolderDto[] }) {
  return (
    <div className="overflow-x-auto rounded border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-muted/40 text-left text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Name</th>
            <th className="px-3 py-2 font-medium">Sensitivity</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Size</th>
            <th className="px-3 py-2 font-medium">{isTrash ? 'Deleted' : 'Updated'}</th>
            <th className="px-3 py-2 font-medium" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {childFolders.map((f) => (
            <FolderTableRow
              key={f.folderId}
              folder={f}
              onOpen={() => onOpenFolder(f.folderId)}
              onDropDocument={onDropOnFolder ? (id) => onDropOnFolder(f.folderId, id) : undefined}
            />
          ))}
          {documents.map((d) => (
            <tr
              key={d.documentId}
              draggable={!isTrash}
              onDragStart={
                isTrash
                  ? undefined
                  : (e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData(DRAG_DOC_MIME, d.documentId);
                    }
              }
              className="cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/30"
              onClick={() => onOpenDocument(d)}
            >
              <td className="px-3 py-2 text-foreground">
                <div className="flex flex-col">
                  <span className="truncate font-medium">{d.name}</span>
                  {locationOf && (
                    <span className="truncate text-xs text-muted-foreground">{locationOf(d)}</span>
                  )}
                  {d.tags.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {d.tags.slice(0, 4).join(', ')}
                      {d.tags.length > 4 ? '…' : ''}
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-1">
                  <SensitivityBadge sensitivity={d.sensitivity} />
                  <VisibilityBadge visibility={d.visibility} />
                  <EditedByAppBadge lastWrittenByArcheId={d.lastWrittenByArcheId} />
                </div>
              </td>
              <td className="px-3 py-2 text-muted-foreground">{d.status}</td>
              <td className="px-3 py-2 text-muted-foreground">{formatBytes(d.sizeBytes)}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {new Date(
                  isTrash ? (d.deletedAt ?? d.updatedAt) : d.updatedAt,
                ).toLocaleDateString()}
              </td>
              <td className="px-3 py-2 text-right">
                <RowActions actions={actionsFor(d)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Folder row in the table view. Renders with the same column layout
 * as a document row so the table doesn't visually fracture, but the
 * empty cells make the difference obvious. Click navigates into the
 * folder (same as the grid's FolderTile); folder management lives in
 * the left rail. Drop target for the same documentId drag-and-drop. */
interface FolderTableRowProps {
  folder: VaultDocumentFolderDto;
  onOpen: () => void;
  onDropDocument?: (documentId: string) => void;
}

export function FolderTableRow({ folder, onOpen, onDropDocument }: FolderTableRowProps) {
  const { dropHover, isDragSource, dndProps } = useFolderRowDnd({
    dropTarget: folder.folderId,
    dragFolder: folder,
    onDropDocument,
  });
  return (
    <tr
      onClick={onOpen}
      {...dndProps}
      className={`cursor-pointer border-b border-border last:border-b-0 hover:bg-muted/30 ${
        dropHover ? 'bg-primary/5 ring-1 ring-inset ring-primary/40' : ''
      } ${isDragSource ? 'opacity-50' : ''}`}
    >
      <td className="px-3 py-2 text-foreground">
        <div className="flex items-center gap-2">
          <FolderIcon
            className={`h-4 w-4 shrink-0 ${
              folder.isProtected ? 'text-muted-foreground' : 'text-primary/70'
            }`}
          />
          <span className="truncate font-medium">{folder.name}</span>
          {folder.isProtected && (
            <Lock className="h-3 w-3 text-muted-foreground" aria-label="System folder" />
          )}
        </div>
      </td>
      <td className="px-3 py-2" />
      <td className="px-3 py-2 text-muted-foreground">Folder</td>
      <td className="px-3 py-2" />
      <td className="px-3 py-2" />
      <td className="px-3 py-2" />
    </tr>
  );
}

/** The buttons for a file's actions. Renders only what the action set
 *  offers — whether a file may move, be shared with an arche, or be deleted
 *  here was decided by `useFileActions`. */
export function RowActions({ actions }: { actions: FileActionSet }) {
  const stop = (handler?: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler?.();
  };
  const btn =
    'rounded border border-border bg-card p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors';
  const dangerBtn =
    'rounded border border-destructive/40 bg-card p-1 text-destructive hover:bg-destructive/10 transition-colors';
  return (
    <div className="flex items-center justify-end gap-1">
      <button
        type="button"
        aria-label="View details"
        title="View details"
        onClick={stop(actions.viewDetails)}
        className={btn}
      >
        <Info className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Download"
        title="Download"
        onClick={stop(actions.download)}
        className={btn}
      >
        <DownloadIcon className="h-4 w-4" />
      </button>
      {actions.move && (
        <button
          type="button"
          aria-label="Move to folder"
          title="Move to folder"
          onClick={stop(actions.move)}
          className={btn}
        >
          <ArrowRightLeft className="h-4 w-4" />
        </button>
      )}
      {actions.giveArcheAccess && (
        <button
          type="button"
          aria-label="Give an arche access"
          title="Give an arche access"
          onClick={stop(actions.giveArcheAccess)}
          className={btn}
        >
          <ShieldCheck className="h-4 w-4" />
        </button>
      )}
      {actions.restore && (
        <button
          type="button"
          aria-label="Restore"
          title="Restore from trash"
          onClick={stop(actions.restore)}
          className={btn}
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      )}
      {actions.softDelete && (
        <button
          type="button"
          aria-label="Move to trash"
          title="Move to trash"
          onClick={stop(actions.softDelete)}
          className={dangerBtn}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {actions.purge && (
        <button
          type="button"
          aria-label="Delete permanently"
          title="Delete permanently"
          onClick={stop(actions.purge)}
          className={dangerBtn}
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grid view — Windows Explorer style tiles
// ---------------------------------------------------------------------------

interface DocumentsGridProps extends DocumentsViewProps {
  childFolders: VaultDocumentFolderDto[];
}

function DocumentsGrid({
  documents,
  isTrash,
  childFolders,
  onOpenDocument,
  onOpenFolder,
  actionsFor,
  locationOf,
  onDropOnFolder,
}: DocumentsGridProps) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {childFolders.map((f) => (
        <FolderTile
          key={f.folderId}
          folder={f}
          onOpen={() => onOpenFolder(f.folderId)}
          onDropDocument={onDropOnFolder ? (id) => onDropOnFolder(f.folderId, id) : undefined}
        />
      ))}
      {documents.map((d) => (
        <DocumentTile
          key={d.documentId}
          document={d}
          isTrash={isTrash}
          location={locationOf?.(d)}
          onOpen={() => onOpenDocument(d)}
          actions={actionsFor(d)}
        />
      ))}
    </div>
  );
}

interface FolderTileProps {
  folder: VaultDocumentFolderDto;
  onOpen: () => void;
  onDropDocument?: (documentId: string) => void;
}

export function FolderTile({ folder, onOpen, onDropDocument }: FolderTileProps) {
  const { dropHover, isDragSource, isDraggable, dndProps } = useFolderRowDnd({
    dropTarget: folder.folderId,
    dragFolder: folder,
    onDropDocument,
  });
  return (
    // A div rather than a <button> for the same reason DocumentTile is:
    // Firefox won't start a drag from a form control, so a draggable
    // tile has to be a plain element with the button role added back.
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      {...dndProps}
      className={`flex cursor-pointer flex-col items-center gap-2 rounded border border-border bg-card p-4 text-center transition hover:border-primary/40 hover:bg-muted/30 ${
        dropHover ? 'ring-2 ring-primary/60 bg-primary/5' : ''
      } ${isDragSource ? 'opacity-50' : ''} ${
        isDraggable ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
    >
      <FolderIcon
        className={`h-12 w-12 ${folder.isProtected ? 'text-muted-foreground' : 'text-primary/70'}`}
      />
      <span className="line-clamp-2 break-words text-xs font-medium text-foreground">
        {folder.name}
      </span>
      {folder.isProtected && (
        <Lock className="h-3 w-3 text-muted-foreground" aria-label="System folder" />
      )}
    </div>
  );
}

interface DocumentTileProps {
  document: VaultDocumentDto;
  isTrash: boolean;
  /** Where it sits, for views that span folders. */
  location?: string;
  onOpen: () => void;
  actions: FileActionSet;
}

function DocumentTile({ document: doc, isTrash, location, onOpen, actions }: DocumentTileProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      draggable={!isTrash}
      onDragStart={
        isTrash
          ? undefined
          : (e) => {
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData(DRAG_DOC_MIME, doc.documentId);
            }
      }
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group flex cursor-pointer flex-col gap-2 rounded border border-border bg-card p-2 transition hover:border-primary/40 hover:shadow-sm"
    >
      <div className="relative flex h-32 items-center justify-center overflow-hidden rounded bg-muted/40">
        {isImageMime(doc.mimeType) ? (
          <DocumentThumbnail documentId={doc.documentId} name={doc.name} />
        ) : (
          <FileIcon className="h-12 w-12 text-muted-foreground" />
        )}
        {/* Which arche made it (file areas §4.9). An arche's name can be
            contributor-authored, so it is scrubbed before display. */}
        {doc.sourceArcheName && (
          <span
            className="absolute left-1 top-1 max-w-[85%] truncate rounded bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground shadow-sm"
            title={`Made by ${scrubForDisplay(doc.sourceArcheName, 120)}`}
          >
            {scrubForDisplay(doc.sourceArcheName, 40)}
          </span>
        )}
      </div>
      <div className="flex min-h-[2.5rem] flex-col">
        <span className="line-clamp-2 break-words text-xs font-medium text-foreground">
          {doc.name}
        </span>
        {location && (
          <span className="mt-0.5 truncate text-[10px] text-muted-foreground" title={location}>
            {location}
          </span>
        )}
        <span className="mt-0.5 text-[10px] text-muted-foreground">
          {formatBytes(doc.sizeBytes)}
        </span>
      </div>
      <div className="opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
        <RowActions actions={actions} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View-mode toggle
// ---------------------------------------------------------------------------

export function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: 'list' | 'grid';
  onChange: (mode: 'list' | 'grid') => void;
}) {
  const base =
    'rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground transition-colors';
  const active = 'bg-muted text-foreground';
  return (
    <div className="flex items-center rounded border border-border bg-card p-0.5">
      <button
        type="button"
        aria-label="Grid view"
        title="Grid view"
        onClick={() => onChange('grid')}
        className={`${base} ${mode === 'grid' ? active : ''}`}
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="List view"
        title="List view"
        onClick={() => onChange('list')}
        className={`${base} ${mode === 'list' ? active : ''}`}
      >
        <ListIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
